import type { Discipline } from './contracts'
import { dateKey, previousDay } from './workout'

/**
 * История Hone Score по дням.
 *
 * Храним по одному снимку на дату, а не лог каждой сессии: для графика
 * недели нужен день, а CloudStorage ограничен 4096 байтами на значение.
 * Тридцати дней хватает на месячный тренд и укладывается в лимит.
 */

export interface DaySnapshot {
  /** Локальная дата ГГГГ-ММ-ДД */
  readonly date: string
  readonly total: number | null
  /**
   * Разбивка по дисциплинам. Записью, а не отдельными полями:
   * дисциплины добавляются, а старые снимки должны читаться дальше.
   */
  readonly byDiscipline?: Partial<Record<Discipline, number | null>>
}

export const KEEP_DAYS = 30

/**
 * Записать снимок за день.
 * Повторный вызов за ту же дату заменяет запись, а не добавляет вторую -
 * поэтому функцию безопасно звать при каждой загрузке экрана.
 */
export function recordDay(
  history: readonly DaySnapshot[],
  snapshot: DaySnapshot,
  keep: number = KEEP_DAYS,
): DaySnapshot[] {
  const without = history.filter((d) => d.date !== snapshot.date)
  return [...without, snapshot].sort((a, b) => a.date.localeCompare(b.date)).slice(-keep)
}

/**
 * Ряд последних N дней, от старого к новому.
 * Дни без тренировки дают null - на графике это пропуск, а не ноль:
 * отсутствие данных и нулевой результат - разные вещи.
 */
export function daySeries(
  history: readonly DaySnapshot[],
  today: string = dateKey(),
  days = 7,
): (DaySnapshot | null)[] {
  const byDate = new Map(history.map((d) => [d.date, d]))
  const out: (DaySnapshot | null)[] = []

  let cursor = today
  for (let i = 0; i < days; i++) {
    out.unshift(byDate.get(cursor) ?? null)
    cursor = previousDay(cursor)
    if (cursor === '') break
  }
  return out
}

/** Изменение индекса за неделю. null, если сравнивать не с чем. */
export function weekChange(
  history: readonly DaySnapshot[],
  today: string = dateKey(),
): number | null {
  const series = daySeries(history, today, 7).filter(
    (d): d is DaySnapshot => d !== null && d.total !== null,
  )
  const first = series[0]
  const last = series[series.length - 1]
  if (!first || !last || series.length < 2) return null
  return (last.total as number) - (first.total as number)
}

/** Границы для графика. Узкий диапазон делает мелкие изменения заметными. */
export function chartRange(values: readonly number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 85, max: 115 }
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  // всегда показываем хотя бы 20 пунктов шкалы, иначе один результат
  // растягивается на всю высоту и выглядит как рекорд
  const pad = Math.max((20 - (hi - lo)) / 2, 2)
  return { min: Math.floor(lo - pad), max: Math.ceil(hi + pad) }
}

/** Высота столбика в процентах. */
export function barHeight(value: number, range: { min: number; max: number }): number {
  const span = range.max - range.min
  if (span <= 0) return 50
  return Math.min(100, Math.max(4, ((value - range.min) / span) * 100))
}
